import { useContext, useEffect, useState } from "react";
import { Box, Typography } from "@mui/material";
import Carousel from "react-elastic-carousel";
import { useTranslation } from "react-i18next";
import { GlobalContext } from "../../../../common/Context";
import LoadingWheel from "../../../../common/LoadingWheel";
import ReBox from "../../../../common/ReBox";
import { green, white } from "../../../../../theme/colours";
import { TrophyIcon } from "../../../../common/Icons";
import GrandPrizeInfoArea from "../GrandPrizeInfoArea";
import { CHALLENGES_PRIZE_AREA } from "../../../../../constants/ids";

const GrandPrizeArea = ({
  gpInfo,
  currentBanners,
  renderMain,
  renderGP,
  rerender,
  setRerender,
  setDialogInfo,
  setOpenDialog,
}) => {
  const { t } = useTranslation("CompetitionInfo");
  const { mobileView, tabletView, desktopView } = useContext(GlobalContext);

  const [currentView, setCurrentView] = useState("");
  const [bannerHeight, setBannerHeight] = useState(100);

  const changeHeight = () => {
    if (gpInfo) {
      const element = document.getElementById("gpBanner");
      element && setBannerHeight(element.clientHeight);
    }
  };

  const throttleDebounce = (fn, ms) => {
    let lastCall = 0;
    let timeoutId;

    return (...args) => {
      const now = new Date().getTime();

      if (now - lastCall < ms) {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => {
          lastCall = now;
          fn(...args);
        }, ms);
      } else {
        lastCall = now;
        fn(...args);
      }
    };
  };

  // Image sometimes loads with height of 0 so retry until height is not 0
  const getHeight = () => {
    setTimeout(() => {
      const element = document.getElementById("gpBanner");
      const height = element.clientHeight;
      if (height === 0) {
        return getHeight();
      }
      setBannerHeight(element.clientHeight);
      return;
    }, 100);
  };

  // Throttles resize events to only be sent every 200 ms to prevent a bombardment of events
  useEffect(() => {
    window.addEventListener("resize", throttleDebounce(changeHeight, 200));
    return () => {
      window.removeEventListener("resize", throttleDebounce(changeHeight, 200));
    };
  }, []);

  useEffect(() => {
    if (desktopView) {
      setCurrentView("desktop");
      return;
    }
    if (tabletView) {
      setCurrentView("tablet");
      return;
    }
    setCurrentView("mobile");
  }, [mobileView, tabletView, desktopView]);

  const prizeAreaStyle = {
    display: renderMain() && !renderGP() ? "none" : "flex",
    flexDirection: "column",
    alignItems: "center",
    width: tabletView || mobileView ? "100%" : "33%",
    height: desktopView && "25rem",
    background: !renderGP() && white,
    borderRadius: "12px",
    boxShadow: !renderGP() && "0px 2px 3px 1px #dad8e8",
  };
  const upperContentStyle = {
    padding: mobileView || tabletView ? "0.25rem 1rem" : "0.5rem 2rem",
    borderRadius: "0 0 1rem 1rem",
    display: "flex",
    flexDirection: "column",
    width: "100%",
    flex: 1,
    ...(!desktopView
      ? {
          background: "none",
          boxShadow: "none",
          p: 0,
        }
      : {}),
  };
  const upperContentStyleCarousel = {
    padding: mobileView || tabletView ? "0.25rem 1rem" : "0 0 0.5rem 0",
    borderRadius: "1rem",
    "& .rec-slider-container": {
      margin: 0,
    },
    "& .rec-pagination": {
      marginTop: 0,
    },
    "& .rec-item-wrapper": {
      justifyContent: "left",
    },
  };
  const upperHeaderPrizeStyle = {
    display: "flex",
    flexDirection: "row",
    alignContent: "flex-start",
    justifyContent: "space-between",
    alignItems: "center",
    height: "3rem",
    mb: desktopView && "1rem",
    my: tabletView ? "1rem" : mobileView && "1.5rem",
  };
  const upperHeaderPrizeStyleCarousel = {
    position: "absolute",
    top: `calc(${bannerHeight}px + 0.5rem)`,
    pl: desktopView ? "2rem" : "0",
  };
  const gpTitleAreaStyle = {
    display: "flex",
    alignItems: "center",
  };
  const iconStyle = {
    width: "25px",
    fill: green[2],
  };
  const grandPrizeStyle = {
    ml: "0.5rem",
    fontSize: desktopView ? "1.5rem" : "1.25rem",
    color: green[2],
    fontWeight: 700,
  };
  const carouselItemStyle = {
    display: "flex",
    flexDirection: "column",
    width: "100%",
  };
  const gpBannerStyle = {
    borderRadius: desktopView ? "10px 10px 0 0" : tabletView && "10px",
    width: mobileView && gpInfo?.length <= 1 ? "100vw" : "100%",
    mt: mobileView && "-3.5rem",
    m: 0,
    userDrag: "none",
    "-webkit-user-drag": "none",
    userSelect: "none",
    "-moz-user-select": "none",
    "-webkit-user-select": "none",
    "-ms-user-select": "none",
  };
  const grandPrizeBoxStyle = {
    px: desktopView ? "2rem" : "0",
    mt: desktopView ? "3rem" : "6rem",
  };
  const noCampBoxStyle = {
    display: "flex",
    justifyContent: "center",
    flexDirection: "column",
    alignContent: "center",
    textAlign: "center",
    height: "100%",
    padding: "1rem",
  };
  const emptyTextStyle = {
    fontWeight: 500,
  };

  return (
    <Box sx={prizeAreaStyle} id={CHALLENGES_PRIZE_AREA}>
      {currentBanners.prize ? (
        renderGP() ? (
          <>
            {gpInfo.length > 1 ? (
              <ReBox
                style={{ ...upperContentStyle, ...upperContentStyleCarousel }}>
                <Box
                  sx={{
                    ...upperHeaderPrizeStyle,
                    ...upperHeaderPrizeStyleCarousel,
                  }}>
                  <Box sx={gpTitleAreaStyle}>
                    <TrophyIcon style={iconStyle} />
                    <Typography sx={grandPrizeStyle}>
                      {gpInfo.length > 1 ? t("grandPrizes") : t("grandPrize")}
                    </Typography>
                  </Box>
                </Box>
                <Carousel showArrows={false}>
                  {gpInfo.map((gp, index) => (
                    <Box key={index} sx={carouselItemStyle}>
                      {index === 0 ? (
                        <Box
                          id={"gpBanner"}
                          component="img"
                          alt={t("gpBannerDesc")}
                          src={currentBanners.prize[gp.id]?.[currentView]}
                          onLoad={getHeight}
                          sx={gpBannerStyle}
                        />
                      ) : (
                        <Box
                          component="img"
                          alt={t("gpBannerDesc")}
                          src={currentBanners.prize[gp.id]?.[currentView]}
                          sx={gpBannerStyle}
                        />
                      )}
                      <Box sx={grandPrizeBoxStyle}>
                        <GrandPrizeInfoArea
                          key={index}
                          gp={gp}
                          rerender={rerender}
                          setRerender={setRerender}
                          setDialogInfo={setDialogInfo}
                          setOpenDialog={setOpenDialog}
                          inCarousel={true}
                        />
                      </Box>
                    </Box>
                  ))}
                </Carousel>
              </ReBox>
            ) : (
              <>
                <Box
                  component="img"
                  alt={t("gpBannerDesc")}
                  src={currentBanners.prize[gpInfo[0].id][currentView]}
                  sx={gpBannerStyle}
                />
                <ReBox style={upperContentStyle}>
                  <Box sx={upperHeaderPrizeStyle}>
                    <Box sx={gpTitleAreaStyle}>
                      <TrophyIcon style={{ width: "25px", fill: green[2] }} />
                      <Typography sx={grandPrizeStyle}>
                        {gpInfo.length > 1 ? t("grandPrizes") : t("grandPrize")}
                      </Typography>
                    </Box>
                  </Box>
                  <GrandPrizeInfoArea
                    gp={gpInfo[0]}
                    rerender={rerender}
                    setRerender={setRerender}
                    setDialogInfo={setDialogInfo}
                    setOpenDialog={setOpenDialog}
                    inCarousel={true}
                  />
                </ReBox>
              </>
            )}
          </>
        ) : (
          <Box sx={noCampBoxStyle}>
            <Typography variant="h3" sx={emptyTextStyle}>
              {t("EmptyGPTitle")}
            </Typography>
            <Typography variant="body1" sx={emptyTextStyle}>
              {t("EmptyGPDescription")}
            </Typography>
          </Box>
        )
      ) : (
        <LoadingWheel width={3} />
      )}
    </Box>
  );
};

export default GrandPrizeArea;
